import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Select,
  Badge,
  Pagination,
  Alert,
} from '../../components/ui';
import {
  getPegawaiList,
  getPegawai,
  createPegawai,
  updatePegawai,
  deletePegawai,
  getSekolahList,
  getJabatanList,
  getRoles,
} from '../../features/master/api';
import {
  RoleCode,
  type PegawaiListItem,
  type PegawaiDetail,
  type Sekolah,
  type Jabatan,
  type Role,
} from '@app/shared';
import { useAuth } from '../../features/auth/use-auth';

const today = () => new Date().toISOString().slice(0, 10);

type FormData = {
  nama: string;
  nip_nik: string;
  jenis_kelamin: 'L' | 'P';
  tempat_lahir: string;
  tanggal_lahir: string;
  nomor_telepon: string;
  alamat: string;
  nama_bank: string;
  nomor_rekening: string;
  atas_nama_rekening: string;
  status_kepegawaian: 'tetap' | 'kontrak' | 'honorer' | 'magang';
  tanggal_bergabung: string;
  status_aktif: boolean;
  sekolah_ids: number[];
  jabatan_ids: number[];
  akun: {
    buat_akun: boolean;
    username: string;
    email: string;
    password: string;
    role_code: '' | 'admin' | 'guru' | 'kepala_sekolah';
    status_akun: 'aktif' | 'nonaktif' | 'terkunci';
  };
};

function toForm(detail?: PegawaiDetail | null): FormData {
  const akun = detail?.akun;
  return {
    nama: detail?.nama ?? '',
    nip_nik: detail?.nip_nik ?? '',
    jenis_kelamin: detail?.jenis_kelamin ?? 'L',
    tempat_lahir: detail?.tempat_lahir ?? '',
    tanggal_lahir: detail?.tanggal_lahir ?? '',
    nomor_telepon: detail?.nomor_telepon ?? '',
    alamat: detail?.alamat ?? '',
    nama_bank: detail?.nama_bank ?? '',
    nomor_rekening: detail?.nomor_rekening ?? '',
    atas_nama_rekening: detail?.atas_nama_rekening ?? '',
    status_kepegawaian: detail?.status_kepegawaian ?? 'honorer',
    tanggal_bergabung: detail?.tanggal_bergabung ?? today(),
    status_aktif: detail?.status_aktif ?? true,
    sekolah_ids: detail?.penugasan_sekolah.map((s) => s.sekolah_id) ?? [],
    jabatan_ids: detail?.jabatan.map((j) => j.jabatan_id) ?? [],
    akun: {
      buat_akun: Boolean(akun),
      username: akun?.username ?? '',
      email: akun?.email ?? '',
      password: '',
      role_code: (akun?.roles[0] as FormData['akun']['role_code']) ?? '',
      status_akun: akun?.status_akun ?? 'aktif',
    },
  };
}

function ListView({ onDetail, canManage }: { onDetail: (id: number) => void; canManage: boolean }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PegawaiListItem[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sekolahOpts, setSekolahOpts] = useState<Sekolah[]>([]);
  const [jabatanOpts, setJabatanOpts] = useState<Jabatan[]>([]);
  const [sekolahId, setSekolahId] = useState('');
  const [jabatanId, setJabatanId] = useState('');

  const load = async (page = 1) => {
    setLoading(true);
    try {
      const [res, sekolahRes, jabatanRes] = await Promise.all([
        getPegawaiList({
          page,
          limit: 10,
          search: search || undefined,
          status_aktif: status ? status === 'true' : undefined,
          sekolah_id: sekolahId ? Number(sekolahId) : undefined,
          jabatan_id: jabatanId ? Number(jabatanId) : undefined,
        }),
        getSekolahList({ limit: 100 }),
        getJabatanList({ limit: 100 }),
      ]);
      setRows(res.rows);
      setMeta(res.meta);
      setSekolahOpts(sekolahRes.rows);
      setJabatanOpts(jabatanRes.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [search, status, sekolahId, jabatanId]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Yakin ingin menghapus pegawai ini?')) return;
    try {
      await deletePegawai(id);
      load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus');
    }
  };

  return (
    <Card>
      {error && <Alert type="error">{error}</Alert>}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <Input
          placeholder="Cari nama/NIP..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '180px' }}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ width: '140px' }}
        >
          <option value="">Semua status</option>
          <option value="true">Aktif</option>
          <option value="false">Nonaktif</option>
        </Select>
        <Select
          value={sekolahId}
          onChange={(e) => setSekolahId(e.target.value)}
          style={{ width: '160px' }}
        >
          <option value="">Semua sekolah</option>
          {sekolahOpts.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nama}
            </option>
          ))}
        </Select>
        <Select
          value={jabatanId}
          onChange={(e) => setJabatanId(e.target.value)}
          style={{ width: '160px' }}
        >
          <option value="">Semua jabatan</option>
          {jabatanOpts.map((j) => (
            <option key={j.id} value={j.id}>
              {j.nama}
            </option>
          ))}
        </Select>
        {canManage && (
          <Button onClick={() => navigate('/master/pegawai/new')}>Tambah Pegawai</Button>
        )}
      </div>
      <Table>
        <thead>
          <tr>
            <Th>NIP/NIK</Th>
            <Th>Nama</Th>
            <Th>Sekolah</Th>
            <Th>Jabatan</Th>
            <Th>Kepegawaian</Th>
            <Th>Status</Th>
            <Th style={{ textAlign: 'right' }}>Aksi</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <Td>{row.nip_nik || '-'}</Td>
              <Td>{row.nama}</Td>
              <Td>{row.sekolah.map((s) => s.nama).join(', ') || '-'}</Td>
              <Td>{row.jabatan.map((j) => j.nama).join(', ') || '-'}</Td>
              <Td>{row.status_kepegawaian}</Td>
              <Td>
                <Badge color={row.status_aktif ? 'green' : 'gray'}>
                  {row.status_aktif ? 'Aktif' : 'Nonaktif'}
                </Badge>
              </Td>
              <Td style={{ textAlign: 'right' }}>
                <Button variant="secondary" onClick={() => onDetail(row.id)}>
                  Detail
                </Button>{' '}
                {canManage && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/master/pegawai/${row.id}/edit`)}
                    >
                      Ubah
                    </Button>{' '}
                    <Button variant="danger" onClick={() => handleDelete(row.id)}>
                      Hapus
                    </Button>
                  </>
                )}
              </Td>
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr>
              <Td colSpan={7} style={{ textAlign: 'center' }}>
                Tidak ada data
              </Td>
            </tr>
          )}
        </tbody>
      </Table>
      {loading && <p style={{ textAlign: 'center', marginTop: '1rem' }}>Memuat...</p>}
      <Pagination page={meta.page} totalPages={meta.total_pages} onChange={load} />
    </Card>
  );
}

function DetailView({
  id,
  onBack,
  canManage,
}: {
  id: number;
  onBack: () => void;
  canManage: boolean;
}) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PegawaiDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    getPegawai(id)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'Gagal memuat'));
  }, [id]);
  if (error) return <Alert type="error">{error}</Alert>;
  if (!detail) return <p>Memuat...</p>;
  return (
    <Card>
      <h3>{detail.nama}</h3>
      <p>
        <strong>NIP/NIK:</strong> {detail.nip_nik || '-'}
      </p>
      <p>
        <strong>Jenis Kelamin:</strong> {detail.jenis_kelamin || '-'}
      </p>
      <p>
        <strong>No. HP:</strong> {detail.nomor_telepon || '-'}
      </p>
      <p>
        <strong>Alamat:</strong> {detail.alamat || '-'}
      </p>
      <p>
        <strong>Status Kepegawaian:</strong> {detail.status_kepegawaian}
      </p>
      <p>
        <strong>Tempat/Tanggal Lahir:</strong>{' '}
        {[detail.tempat_lahir, detail.tanggal_lahir].filter(Boolean).join(', ') || '-'}
      </p>
      <p>
        <strong>Bank:</strong>{' '}
        {[detail.nama_bank, detail.nomor_rekening, detail.atas_nama_rekening]
          .filter(Boolean)
          .join(' / ') || '-'}
      </p>
      <p>
        <strong>Tanggal Bergabung:</strong> {detail.tanggal_bergabung}
      </p>
      {detail.akun && (
        <p>
          <strong>Akun:</strong> {detail.akun.username} ({detail.akun.roles.join(', ')}) -{' '}
          <Badge color={detail.akun.status_akun === 'aktif' ? 'green' : 'gray'}>
            {detail.akun.status_akun}
          </Badge>
        </p>
      )}
      <p>
        <strong>Status:</strong>{' '}
        <Badge color={detail.status_aktif ? 'green' : 'gray'}>
          {detail.status_aktif ? 'Aktif' : 'Nonaktif'}
        </Badge>
      </p>
      <p>
        <strong>Sekolah:</strong> {detail.penugasan_sekolah.map((s) => s.nama).join(', ') || '-'}
      </p>
      <p>
        <strong>Jabatan:</strong> {detail.jabatan.map((j) => j.nama).join(', ') || '-'}
      </p>
      {detail.pengaturan_honor && (
        <p>
          <strong>Honor:</strong> JP {detail.pengaturan_honor.tarif_honor_per_jp} | Hadir{' '}
          {detail.pengaturan_honor.tarif_kehadiran_per_hari}
        </p>
      )}
      <div style={{ marginTop: '1rem' }}>
        <Button variant="secondary" onClick={onBack}>
          Kembali
        </Button>{' '}
        {canManage && (
          <>
            <Button variant="secondary" onClick={() => navigate(`/master/pegawai/${id}/edit`)}>
              Ubah
            </Button>{' '}
            <Button
              variant="danger"
              onClick={async () => {
                if (window.confirm('Hapus?')) {
                  await deletePegawai(id);
                  onBack();
                }
              }}
            >
              Hapus
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

function FormView({ id, onDone }: { id?: number; onDone: () => void }) {
  const isEdit = Boolean(id);
  const [form, setForm] = useState<FormData>(toForm());
  const [sekolahOpts, setSekolahOpts] = useState<Sekolah[]>([]);
  const [jabatanOpts, setJabatanOpts] = useState<Jabatan[]>([]);
  const [roleOpts, setRoleOpts] = useState<Role[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    setLoadingOptions(true);
    Promise.all([getSekolahList({ limit: 100 }), getJabatanList({ limit: 100 }), getRoles()])
      .then(([s, j, r]) => {
        setSekolahOpts(s.rows);
        setJabatanOpts(j.rows);
        setRoleOpts(r);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Gagal memuat data'))
      .finally(() => setLoadingOptions(false));
    if (id) {
      getPegawai(id)
        .then((d) => setForm(toForm(d)))
        .catch((e) => setError(e instanceof Error ? e.message : 'Gagal memuat'));
    }
  }, [id]);

  const toggle = (list: number[], id: number) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nama.trim()) {
      setError('Nama wajib diisi');
      return;
    }
    if (form.sekolah_ids.length === 0) {
      setError('Pilih minimal satu sekolah');
      return;
    }
    if (form.jabatan_ids.length === 0) {
      setError('Pilih minimal satu jabatan');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      nip_nik: form.nip_nik.trim(),
      nama: form.nama.trim(),
      jenis_kelamin: form.jenis_kelamin,
      tempat_lahir: form.tempat_lahir.trim() || null,
      tanggal_lahir: form.tanggal_lahir || null,
      nomor_telepon: form.nomor_telepon || null,
      alamat: form.alamat || null,
      nama_bank: form.nama_bank.trim() || null,
      nomor_rekening: form.nomor_rekening.trim() || null,
      atas_nama_rekening: form.atas_nama_rekening.trim() || null,
      status_kepegawaian: form.status_kepegawaian,
      tanggal_bergabung: form.tanggal_bergabung,
      penugasan_sekolah: form.sekolah_ids.map((sekolah_id) => ({
        sekolah_id,
        tanggal_mulai: today(),
      })),
      jabatan: form.jabatan_ids.map((jabatan_id) => ({ jabatan_id, tanggal_mulai: today() })),
    };
    const akunPayload =
      form.akun.buat_akun && form.akun.role_code
        ? {
            username: form.akun.username.trim(),
            email: form.akun.email.trim() || null,
            password: form.akun.password,
            role_code: form.akun.role_code,
          }
        : undefined;
    try {
      if (isEdit && id) {
        const updatePayload: Parameters<typeof updatePegawai>[1] = {
          ...payload,
          status_aktif: form.status_aktif,
        };
        if (akunPayload) {
          updatePayload.akun = {
            username: akunPayload.username,
            email: akunPayload.email,
            ...(akunPayload.password ? { password: akunPayload.password } : {}),
            role_code: akunPayload.role_code,
            status_akun: form.akun.status_akun,
          };
        }
        await updatePegawai(id, updatePayload);
      } else {
        await createPegawai({ ...payload, ...(akunPayload ? { akun: akunPayload } : {}) });
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h3>{isEdit ? 'Ubah Pegawai' : 'Tambah Pegawai'}</h3>
      {error && <Alert type="error">{error}</Alert>}
      <form onSubmit={submit}>
        <Input
          label="Nama Lengkap"
          value={form.nama}
          onChange={(e) => setForm({ ...form, nama: e.target.value })}
          required
        />
        <Input
          label="NIP/NIK"
          value={form.nip_nik}
          onChange={(e) => setForm({ ...form, nip_nik: e.target.value })}
        />
        <Select
          label="Jenis Kelamin"
          value={form.jenis_kelamin}
          onChange={(e) =>
            setForm({ ...form, jenis_kelamin: e.target.value as FormData['jenis_kelamin'] })
          }
        >
          <option value="L">Laki-laki</option>
          <option value="P">Perempuan</option>
        </Select>
        <Input
          label="Tempat Lahir"
          value={form.tempat_lahir}
          onChange={(e) => setForm({ ...form, tempat_lahir: e.target.value })}
        />
        <Input
          label="Tanggal Lahir"
          type="date"
          value={form.tanggal_lahir}
          onChange={(e) => setForm({ ...form, tanggal_lahir: e.target.value })}
        />
        <Input
          label="Nomor Telepon"
          value={form.nomor_telepon}
          onChange={(e) => setForm({ ...form, nomor_telepon: e.target.value })}
        />
        <Input
          label="Alamat"
          value={form.alamat}
          onChange={(e) => setForm({ ...form, alamat: e.target.value })}
        />
        <Input
          label="Nama Bank"
          value={form.nama_bank}
          onChange={(e) => setForm({ ...form, nama_bank: e.target.value })}
        />
        <Input
          label="Nomor Rekening"
          value={form.nomor_rekening}
          onChange={(e) => setForm({ ...form, nomor_rekening: e.target.value })}
        />
        <Input
          label="Atas Nama Rekening"
          value={form.atas_nama_rekening}
          onChange={(e) => setForm({ ...form, atas_nama_rekening: e.target.value })}
        />
        <Select
          label="Status Kepegawaian"
          value={form.status_kepegawaian}
          onChange={(e) =>
            setForm({
              ...form,
              status_kepegawaian: e.target.value as FormData['status_kepegawaian'],
            })
          }
        >
          <option value="tetap">Tetap</option>
          <option value="kontrak">Kontrak</option>
          <option value="honorer">Honorer</option>
          <option value="magang">Magang</option>
        </Select>
        <Input
          label="Tanggal Bergabung"
          type="date"
          value={form.tanggal_bergabung}
          onChange={(e) => setForm({ ...form, tanggal_bergabung: e.target.value })}
        />
        <Select
          label="Status Aktif"
          value={String(form.status_aktif)}
          onChange={(e) => setForm({ ...form, status_aktif: e.target.value === 'true' })}
        >
          <option value="true">Aktif</option>
          <option value="false">Nonaktif</option>
        </Select>
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={form.akun.buat_akun}
              onChange={(e) =>
                setForm({
                  ...form,
                  akun: { ...form.akun, buat_akun: e.target.checked },
                })
              }
            />
            {isEdit ? 'Kelola Akun Aplikasi' : 'Buat Akun Aplikasi'}
          </label>
          {form.akun.buat_akun && (
            <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
              <Input
                label="Username"
                value={form.akun.username}
                onChange={(e) =>
                  setForm({
                    ...form,
                    akun: { ...form.akun, username: e.target.value },
                  })
                }
                required
              />
              <Input
                label="Email"
                type="email"
                value={form.akun.email}
                onChange={(e) =>
                  setForm({
                    ...form,
                    akun: { ...form.akun, email: e.target.value },
                  })
                }
              />
              <Input
                label={isEdit ? 'Password Baru (kosongkan jika tidak diubah)' : 'Password'}
                type="password"
                value={form.akun.password}
                onChange={(e) =>
                  setForm({
                    ...form,
                    akun: { ...form.akun, password: e.target.value },
                  })
                }
                required={!isEdit}
              />
              <Select
                label="Role"
                value={form.akun.role_code}
                onChange={(e) =>
                  setForm({
                    ...form,
                    akun: {
                      ...form.akun,
                      role_code: e.target.value as FormData['akun']['role_code'],
                    },
                  })
                }
                required
              >
                <option value="">Pilih role</option>
                {roleOpts
                  .filter((r) => r.status_aktif)
                  .map((r) => (
                    <option key={r.id} value={r.kode}>
                      {r.nama}
                    </option>
                  ))}
              </Select>
              {isEdit && (
                <Select
                  label="Status Akun"
                  value={form.akun.status_akun}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      akun: {
                        ...form.akun,
                        status_akun: e.target.value as FormData['akun']['status_akun'],
                      },
                    })
                  }
                >
                  <option value="aktif">Aktif</option>
                  <option value="nonaktif">Nonaktif</option>
                  <option value="terkunci">Terkunci</option>
                </Select>
              )}
            </div>
          )}
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.25rem',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Sekolah <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              boxSizing: 'border-box',
              minHeight: '42px',
              backgroundColor: loadingOptions ? '#f9fafb' : '#fff',
            }}
          >
            {loadingOptions ? (
              <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
                Memuat data sekolah...
              </div>
            ) : sekolahOpts.length === 0 ? (
              <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                Tidak ada data sekolah. Silakan tambahkan sekolah terlebih dahulu di Master Sekolah.
              </div>
            ) : (
              <>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Pilih satu atau lebih sekolah tempat pegawai bertugas:
                </div>
                {sekolahOpts.map((s) => (
                  <label
                    key={s.id}
                    style={{
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      borderRadius: '0.25rem',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <input
                      type="checkbox"
                      checked={form.sekolah_ids.includes(s.id)}
                      onChange={() =>
                        setForm({ ...form, sekolah_ids: toggle(form.sekolah_ids, s.id) })
                      }
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{s.nama}</span>
                  </label>
                ))}
              </>
            )}
          </div>
          {!loadingOptions && sekolahOpts.length > 0 && form.sekolah_ids.length === 0 && (
            <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
              Pilih minimal satu sekolah
            </div>
          )}
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.25rem',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Jabatan <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              boxSizing: 'border-box',
              minHeight: '42px',
              backgroundColor: loadingOptions ? '#f9fafb' : '#fff',
            }}
          >
            {loadingOptions ? (
              <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
                Memuat data jabatan...
              </div>
            ) : jabatanOpts.length === 0 ? (
              <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                Tidak ada data jabatan. Silakan tambahkan jabatan terlebih dahulu di Master Jabatan.
              </div>
            ) : (
              <>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Pilih satu atau lebih jabatan yang dipegang pegawai:
                </div>
                {jabatanOpts.map((j) => (
                  <label
                    key={j.id}
                    style={{
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      borderRadius: '0.25rem',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <input
                      type="checkbox"
                      checked={form.jabatan_ids.includes(j.id)}
                      onChange={() =>
                        setForm({ ...form, jabatan_ids: toggle(form.jabatan_ids, j.id) })
                      }
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{j.nama}</span>
                  </label>
                ))}
              </>
            )}
          </div>
          {!loadingOptions && jabatanOpts.length > 0 && form.jabatan_ids.length === 0 && (
            <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
              Pilih minimal satu jabatan
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button type="submit" disabled={saving}>
            Simpan
          </Button>
          <Button variant="secondary" onClick={onDone} type="button">
            Batal
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function PegawaiPage() {
  const { user } = useAuth();
  const canManage = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const navigate = useNavigate();
  const location = useLocation();
  const parts = location.pathname.replace('/master/pegawai', '').split('/').filter(Boolean);
  const isCreate = parts[0] === 'new';
  const isEdit = parts[1] === 'edit';
  const numericId = !isCreate && parts[0] ? Number(parts[0]) : undefined;
  const onDone = () => navigate('/master/pegawai');

  return (
    <AdminLayout title="Master Pegawai">
      {parts.length === 0 && (
        <ListView canManage={canManage} onDetail={(i) => navigate(`/master/pegawai/${i}`)} />
      )}
      {numericId && !isEdit && <DetailView id={numericId} onBack={onDone} canManage={canManage} />}
      {(isCreate || isEdit) && canManage && (
        <FormView id={isEdit ? numericId : undefined} onDone={onDone} />
      )}
      {(isCreate || isEdit) && !canManage && (
        <Alert type="error">Anda tidak berhak mengakses halaman ini.</Alert>
      )}
    </AdminLayout>
  );
}
